import React from 'react';
import { LabelTooltipData } from './LabelWithTooltip';
import LabelDetailCard from './LabelDetailCard';
import SignalTag, { TagVariant } from '../shared/SignalTag';

export interface ConductSummaryData {
  // complaint
  complaint?: {
    detected?: boolean;
    explanation_short?: string;
    confidence?: number; // 0.0 - 1.0
    labels?: LabelTooltipData[];
  };
  // vulnerability
  vulnerability?: {
    detected?: boolean;
    explanation_short?: string;
    score?: number; // 0-100
    labels?: LabelTooltipData[];
  };
  // customer strain
  strain?: {
    score?: number; // 0-100
    summary_short?: string;
    labels?: LabelTooltipData[];
  };
  // foreseeable harm
  harm?: {
    score?: number; // 0-100
    summary_short?: string;
    labels?: LabelTooltipData[];
  };
  // consumer duty
  consumer_duty?: {
    summary_short?: string;
    labels?: LabelTooltipData[];
  };
}

interface ConductSummaryBlocksProps {
  data: ConductSummaryData;
  onLabelTap?: (meta: LabelTooltipData) => void;
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="bg-white rounded-lg p-4">
    <div className="text-sm font-semibold text-gray-900 mb-3">{title}</div>
    {children}
  </div>
);

const ConductSummaryBlocks: React.FC<ConductSummaryBlocksProps> = ({ data, onLabelTap }) => {
  const [hoverDetail, setHoverDetail] = React.useState<{ data: LabelTooltipData; dimension: string } | null>(null);
  const [hoverPosition, setHoverPosition] = React.useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [activeIndex, setActiveIndex] = React.useState(0);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  
  const handleMouseEnter = React.useCallback((e: React.MouseEvent, label: LabelTooltipData, dimension: string) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setHoverPosition({
      top: rect.bottom + window.scrollY + 8,
      left: rect.left + window.scrollX
    });
    setHoverDetail({ data: label, dimension });
  }, []);
  
  const handleMouseLeave = React.useCallback(() => {
    setHoverDetail(null);
  }, []);
  
  // Handle scroll to update active indicator
  React.useEffect(() => {
    const handleScroll = () => {
      if (!scrollRef.current) return;
      const scrollLeft = scrollRef.current.scrollLeft;
      const itemWidth = scrollRef.current.offsetWidth * 0.85; // 85vw per item
      const newIndex = Math.round(scrollLeft / itemWidth);
      setActiveIndex(newIndex);
    };
    
    const scrollElement = scrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener('scroll', handleScroll);
      return () => scrollElement.removeEventListener('scroll', handleScroll);
    }
  }, []);
  
  // Function to render labels with hover functionality
  const renderLabels = (labels: LabelTooltipData[] | undefined, sectionTitle: string, variant: TagVariant) => {
    if (!labels || labels.length === 0) {
      return <div className="text-sm text-gray-500">No {sectionTitle.toLowerCase()} labels</div>;
    }
    // Group labels by taxonomy category name (with fallback)
    const groups = labels.reduce<Record<string, LabelTooltipData[]>>((acc, lbl) => {
      const key = lbl.category_name || 'Other';
      (acc[key] = acc[key] || []).push(lbl);
      return acc;
    }, {});

    return (
      <div className="space-y-3">
        {Object.entries(groups).map(([catName, items]) => (
          <div key={catName}>
            <div className="text-[11px] text-gray-500 mb-1">{catName}</div>
            <div className="flex flex-wrap gap-2">
              {items.map((l, idx) => (
                <span
                  key={`${catName}-${idx}`}
                  onMouseEnter={(e) => handleMouseEnter(e, l, sectionTitle)}
                  onMouseLeave={handleMouseLeave}
                  className="cursor-pointer inline-block"
                  role={onLabelTap ? 'button' : undefined}
                  tabIndex={onLabelTap ? 0 : undefined}
                  onClick={() => onLabelTap && onLabelTap(l)}
                  onKeyDown={(e) => {
                    if (!onLabelTap) return;
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onLabelTap(l); }
                  }}
                >
                  <SignalTag label={l.label_name} variant={variant} />
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };
  
  // Create sections array for easier management
  const sections = [
    {
      title: 'Dissatisfaction',
      content: data.complaint?.detected 
        ? renderLabels(data.complaint.labels, 'Dissatisfaction', 'outcome')
        : <div className="text-sm text-gray-500">No complaint detected</div>
    },
    {
      title: 'Vulnerability',
      content: data.vulnerability?.detected 
        ? renderLabels(data.vulnerability.labels, 'Vulnerability', 'vulnerability')
        : <div className="text-sm text-gray-500">No vulnerability detected</div>
    },
    {
      title: 'Customer Strain',
      content: renderLabels(data.strain?.labels, 'Customer Strain', 'strain')
    },
    {
      title: 'Foreseeable Harm',
      content: renderLabels(data.harm?.labels, 'Foreseeable Harm', 'harm')
    },
    {
      title: 'Consumer Duty',
      content: renderLabels(data.consumer_duty?.labels, 'Consumer Duty', 'consumer-duty')
    }
  ];

  return (
    <>
      <div className="relative">
        {/* Desktop: 3 columns, Tablet: 2 columns */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sections.map((section, idx) => (
            <Section key={idx} title={section.title}>
              {section.content}
            </Section>
          ))}
        </div>

        {/* Mobile: Horizontal Scrollable Carousel */}
        <div className="md:hidden">
          <div 
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory [&::-webkit-scrollbar]:hidden"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
          >
            {sections.map((section, idx) => (
              <div key={idx} className="flex-none w-[85vw] snap-center">
                <Section title={section.title}>
                  {section.content}
                </Section>
              </div>
            ))}
          </div>
          {/* Scroll Indicators */}
          <div className="flex justify-center gap-2 mt-2">
            {sections.map((_, idx) => (
              <button
                key={idx}
                onClick={() => {
                  if (scrollRef.current) {
                    const itemWidth = scrollRef.current.offsetWidth * 0.85;
                    scrollRef.current.scrollTo({
                      left: idx * itemWidth,
                      behavior: 'smooth'
                    });
                  }
                }}
                className={`transition-all duration-200 ${
                  idx === activeIndex 
                    ? 'w-6 h-1.5 bg-go-primary rounded-full' 
                    : 'w-1.5 h-1.5 bg-gray-300 rounded-full hover:bg-gray-400'
                }`}
                aria-label={`Go to ${sections[idx].title}`}
              />
            ))}
          </div>
        </div>
      </div>
      
      {/* Hover Detail Card - appears as floating overlay outside of relative container */}
      {hoverDetail && (
        <div style={{
          position: 'fixed',
          top: hoverPosition.top,
          left: hoverPosition.left,
          zIndex: 9999
        }}>
          <LabelDetailCard
            category={hoverDetail.dimension}
            subcategory={hoverDetail.data.category_name}
            title={hoverDetail.data.label_name}
            reasoning={hoverDetail.data.rationale}
            regulation={hoverDetail.data.regulation_reference}
            confidence={typeof hoverDetail.data.confidence === 'number' ? (hoverDetail.data.confidence > 1 ? hoverDetail.data.confidence : hoverDetail.data.confidence * 100) : undefined}
          />
        </div>
      )}
    </>
  );
};

export default ConductSummaryBlocks;
